import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import RunCard from './RunCard';
import './Home.css';

const PublicGroupPage = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!publicId) return;
      try {
        setLoading(true);
        // Firestore rules: for list queries, the query must be constrained so only readable docs match.
        // - If logged in: constrain by ownerId
        // - If not logged in: constrain by visibility=public
        const groupsQuery = user
          ? query(
              collection(db, 'groups'),
              where('ownerId', '==', user.uid),
              where('publicId', '==', publicId),
              limit(1)
            )
          : query(
              collection(db, 'groups'),
              where('visibility', '==', 'public'),
              where('publicId', '==', publicId),
              limit(1)
            );

        const groupsSnap = await getDocs(groupsQuery);
        if (groupsSnap.empty) {
          // If logged in, it might be a public group owned by someone else.
          if (user) {
            const publicQuery = query(
              collection(db, 'groups'),
              where('visibility', '==', 'public'),
              where('publicId', '==', publicId),
              limit(1)
            );
            const publicSnap = await getDocs(publicQuery);
            if (publicSnap.empty) {
              setGroup(null);
              setRuns([]);
              return;
            }
            const g = { id: publicSnap.docs[0].id, ...publicSnap.docs[0].data() };
            setGroup(g);
            const runsQuery = query(
              collection(db, 'runs'),
              where('groupId', '==', g.id),
              where('visibility', '==', 'public')
            );
            const runsSnap = await getDocs(runsQuery);
            const runsData = runsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setRuns(runsData);
            return;
          }
          setGroup(null);
          setRuns([]);
          return;
        }

        const g = { id: groupsSnap.docs[0].id, ...groupsSnap.docs[0].data() };
        setGroup(g);

        const isPublic = g.visibility === 'public';
        const isOwner = !!user && g.ownerId === user.uid;
        if (!isPublic && !isOwner) {
          setRuns([]);
          return;
        }

        const runsQuery = isPublic
          ? query(
              collection(db, 'runs'),
              where('groupId', '==', g.id),
              where('visibility', '==', 'public')
            )
          : query(collection(db, 'runs'), where('groupId', '==', g.id));

        const runsSnap = await getDocs(runsQuery);
        const runsData = runsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRuns(runsData);
      } catch (error) {
        console.error('Error loading group:', error);
        setGroup(null);
        setRuns([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [publicId, user]);

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading-spinner">Loading group...</div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="home-container">
        <div className="home-header">
          <h1>Group not found</h1>
          <p className="home-subtitle">This link may be invalid or the group was removed.</p>
        </div>
      </div>
    );
  }

  const isPublic = group.visibility === 'public';
  const isOwner = !!user && group.ownerId === user.uid;
  if (!isPublic && !isOwner) {
    return (
      <div className="home-container">
        <div className="home-header">
          <h1>{group.name || 'Private Group'}</h1>
          <p className="home-subtitle">
            This group is private. Please sign in to view it.
          </p>
          <button className="browse-button" type="button" onClick={() => navigate('/login')}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>{group.name || 'Untitled Group'}</h1>
        <p className="home-subtitle">
          {group.brand ? `${group.brand} · ` : ''}
          {group.location || ''}
        </p>
      </div>

      {runs.length === 0 ? (
        <div style={{ marginTop: 16, color: '#888' }}>
          No runs in this group yet.
        </div>
      ) : (
        <div className="recent-rides-grid" style={{ marginTop: 16 }}>
          {runs.map((run) => (
            <RunCard
              key={run.id}
              run={run}
              viewerUserId={user?.uid}
              onClick={() => {
                // Public runs can be viewed without login. Private runs will redirect within RideView.
                navigate(`/ride/${run.id}`);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicGroupPage;


